import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showDeleteModal } from "store/slices/modalSlice";
import { getVehicleMakeDropdownlist } from "store/slices/vehicleMakeSlice";
import {
  getVehicleModelList,
  resetFilters,
  setFilter,
  updateVehicleModelStatus,
} from "store/slices/vehicleModelSlice";
import { ORDER_BY_OPTIONS, STATUS_OPTIONS } from "utils/constants";
import { formatOptionsForSelect, formatOptionsName, formatOptionsNameWithLangObject } from "utils/formatOptions";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";

const VehicleMake = () => {
  const { t } = useTranslation();
  const { trash, edit, noPlans } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("vehicles");

  const {
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    items: vehicleModel,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy , makeId },
  } = useSelector((state) => state.vehicleModel);
  const {
    itemsList: vehicleMakeList,
  } = useSelector((state) => state.VehicleMake);

  useEffect(() => {
    getAllData();
    dispatch(getVehicleMakeDropdownlist());
  }, [canView]);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      status: isReset ? -1 : status,
      makeId: isReset ? "" : makeId,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getVehicleModelList(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "makeId",
      placeholder: t("Select Make"),
      type: "singleSelect",
      options: formatOptionsNameWithLangObject(vehicleMakeList),
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Model Name"),
        key: "name",
        render: (value) => (
          <div>
            <div>{value?.en}</div>
            {value?.ar && <div className="text-muted small">{value.ar}</div>}
          </div>
        ),
      },
      {
        header: t("Make Name"),
        key: "makeId",
        render: (value) => (
          <div>
            <div>{value?.name?.en}</div>
            {value?.name?.ar && (
              <div className="text-muted small">{value.name.ar}</div>
            )}
          </div>
        ),
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateVehicleModelStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() => navigate(`/vehicles/model/edit?id=${item._id}`)}
            />
          </>
        )}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Vehicle Model"),
                  message: t(
                    "Are you sure you want to delete this Vehicle Model?",
                  ),
                  itemId: item._id,
                  actionType: "VEHICLE_MODEL",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <h4 className="page_title mb-3">{t("Vehicle Model")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Model Name")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy ,makeId }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        btnText={t("Add Vehicle Model")}
        onBtnClick={() => navigate("/vehicles/model/add")}
      />

      {loading && <TableShimmer rows={12} columns={6} />}

      {!loading && vehicleModel?.length === 0 && (
        <NoDataFound title={t("No Vehicle Model Found")} icon={noPlans} />
      )}
      {!loading && vehicleModel?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={vehicleModel}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default VehicleMake;
